/**
 * @file Vue-related helpers
 */
import { initializeAuthenticationChannel } from '@@/bits/auth_broadcast_channel'
import { getHyphenatedCurrentLocale } from '@@/bits/current_locale'
import device from '@@/bits/device'
import { syncDeviceId } from '@@/bits/device_id_persistence'
import { hasAxeUrlParam, hasPerfUrlParam } from '@@/bits/dev_url_params'
import { DOMContentLoadedPromise } from '@@/bits/dom'
import { importWithRetry, isDynamicAssetLoadError } from '@@/bits/dynamic_import'
import environment from '@@/bits/environment'
import * as errorTracker from '@@/bits/error_tracker'
import fixMobileViewport from '@@/bits/fix_mobile_viewport'
import { isAppUsing, isDebugMode } from '@@/bits/flip'
import window from '@@/bits/global'
import { np__, N__, n__, p__, __ } from '@@/bits/intl'
import { reload } from '@@/bits/location'
import { requestIdleCallback } from '@@/bits/request_idle_callback'

import { injectDesignClassesIfEnabled } from '@@/bits/vue_inject_design_classes'
import type { ApplicationMenuBuilder } from '@@/desktop_shell/useApplicationMenuPublisher'
import '@@/styles/tailwind.css'
import type { Id } from '@@/types'
import { configure as configureArvo } from '@padlet/arvo'
import { isEqual } from 'es-toolkit'
import { isObject } from 'es-toolkit/compat'
import { createPinia, type Pinia as PiniaStore } from 'pinia'
import type { PostHog } from 'posthog-js'
import {
  createApp,
  defineAsyncComponent as vueDefineAsyncComponent,
  watch,
  watchEffect,
  type App,
  type AsyncComponentLoader,
  type AsyncComponentOptions,
  type Component,
  type WatchCallback,
  type WatchEffect,
  type WatchOptions,
  type WatchOptionsBase,
  type WatchSource,
  type WatchStopHandle,
} from 'vue'
import { createStore, type Store as VuexStore, type StoreOptions as VuexStoreOptions } from 'vuex'

// Indicates errors that can be potentially fixed by reloading the page.
class PageReloadableError extends Error {}

interface VueApp extends App {
  $store: VuexStore<any>
  $pinia: PiniaStore
  $posthog?: PostHog
}

interface VueAppOptions {
  // Element to mount on
  el?: string

  // Component config
  rootComponent: Component
  rootComponentProps?: () => Record<string, any>
  loadPlugins?: (app: App) => Promise<void>
  created?: () => void // initialize store
  mounted?: () => void
  beforeMountPromises?: Array<Promise<any>>
  otherVueOptions?: Record<string, any>

  // Store
  store?: VuexStore<any>
  storeOptions?: VuexStoreOptions<any>
  initializeStore?: (store: VuexStore<any>) => void
  usePinia?: boolean
  realtime?: boolean // indicates that the app should connect to realtime as part of setting up vuex store
  /**
   * Use to initialize Pinia store. Use this to setup pinia store with starting data (e.g. from `window`)
   * @param store
   * @returns
   */
  initializePiniaStore?: (store: PiniaStore) => void

  // Error-tracking
  environment?: string
  locale?: string
  release?: string
  // Identifies the originating product surface (e.g. `'sandbox'`). When set,
  // every Sentry event captured during this app's lifetime is tagged with
  // `source: <value>`, including unhandled errors and promise rejections.
  errorTrackingSource?: string

  // Others
  disableAccessibilityChecks?: boolean
  useVueQuery?: boolean

  // Authentication broadcast channel
  // If they are not provided, the default behavior is to reload the page.
  onGlobalLogin?: () => void | Promise<void>
  onGlobalLogout?: () => void | Promise<void>

  /** Only dashboards pass a builder. Other pages still handshake. */
  buildApplicationMenu?: ApplicationMenuBuilder

  switchToMakePage?: () => void
}

// Temporary no-op function. Will be replaced when we move to Vue3.
// // Context: https://v3-migration.vuejs.org/breaking-changes/async-components.html
// export const defineAsyncComponent = (component: any): any => component

// What an async component loader resolves to: a module whose default export is
// the component (a .vue import), or the component itself. A retry that fetched
// one of the component's dependency chunks instead resolves to a namespace with
// neither, and handing that to Vue would mount nothing without any recovery.
function isComponentModule(loaded: unknown): boolean {
  if (typeof loaded === 'function') return true
  if (loaded == null || typeof loaded !== 'object') return false
  const defaultExport = (loaded as { default?: unknown }).default
  if (typeof defaultExport === 'function') return true
  if (defaultExport != null && typeof defaultExport === 'object') return true
  return ['render', 'setup', 'template'].some((key) => key in loaded)
}

function defineAsyncComponent<T extends Component>(source: AsyncComponentLoader<T> | AsyncComponentOptions<T>): T {
  const options: AsyncComponentOptions<T> = typeof source === 'function' ? { loader: source } : source
  const userOnError = options.onError
  const { loader } = options

  return vueDefineAsyncComponent<T>({
    ...options,
    // Transient Vite/ui-dev fetch failures are common under load/HMR. Vue's own
    // `retry()` re-runs the same `import()` specifier, which only replays the
    // rejection cached in the module map (no network request), so retry through
    // importWithRetry's cache-busted URL instead. It also hands a second failure
    // to page-level asset recovery before rejecting, and rejects a retry that
    // fetched something other than a component.
    loader: () => importWithRetry(loader, undefined, { isExpectedModule: isComponentModule }),
    onError(error, retry, fail, attempts): void {
      // An asset failure reaching this point is final: importWithRetry has already
      // spent the one cache-busted retry it gets and handed a second failure to
      // page-level recovery. A custom handler still hears about it, so it can record
      // that the component is not coming, but its `retry` would only replay the
      // module map's cached rejection, so it fails instead.
      const retryOrFail = isDynamicAssetLoadError(error) ? fail : retry
      if (userOnError != null) {
        userOnError(error, retryOrFail, fail, attempts)
        return
      }

      fail()
    },
  })
}

// Deliberately duplicated from bits/model_context rather than imported or extracted:
// importing it would pull in the very chunk the gate below exists to avoid, and a shared
// leaf module is not free either — rollup inlines the value but still emits a bare
// `import "./model_context_ids.js"` into the vue chunk, adding a request to the entry
// graph of every page. The drift guard is that this file's spec and model_context's spec
// each hardcode the string independently, so whichever side moves fails its own test.
const WEBMCP_INLINE_TOOLS_ELEMENT_ID = 'webmcp-anonymous-tools'

/**
 * Registers the WebMCP bridge at module-eval time, as early as this module runs.
 * setupVueApp reaches its own call only after the app mounts, which is why the
 * discovery scan read `document.modelContext` and found it empty.
 *
 * Not a guarantee of running before `load`: the dynamic import below is not part of
 * the entry's load-blocking graph, so `load` can fire with the chunk still in flight.
 * What this buys is the gap between module eval and app mount, which is the ~1s that
 * was losing the race — not a hard ordering against the load event.
 *
 * Only the server-inlined logged-out catalogue qualifies: it is the one that needs
 * neither a `tools/list` round-trip nor a CSRF token, so nothing here runs too early.
 * Everything else still goes through the idle path below, and the once-per-document
 * guard inside registerModelContextTools makes that pass a no-op rather than a
 * duplicate registration.
 *
 * Exported for tests only — the call below is the real entry point.
 */
function registerInlinedModelContextTools(): void {
  if (typeof document === 'undefined') return
  if (!('modelContext' in document || 'modelContext' in navigator)) return
  // The gate that keeps the chunk unfetched on pages without an inlined catalogue.
  // Checked before the import for that reason, not merely as an early return.
  if (document.getElementById(WEBMCP_INLINE_TOOLS_ELEMENT_ID) == null) return

  void import('@@/bits/model_context')
    .then(({ registerModelContextTools }) => registerModelContextTools())
    // WebMCP is a progressive enhancement: if the chunk fails to load or registration
    // throws, the page is unaffected and there is no agent present to tell.
    .catch(() => {})
}

registerInlinedModelContextTools()

async function setupVueApp(options: VueAppOptions): Promise<VueApp | undefined> {
  let vueApp: VueApp | undefined

  try {
    await Promise.all([DOMContentLoadedPromise, ...(options.beforeMountPromises ?? [])])

    // After DOMContentLoaded so window.ww.vueStartingState (flip source) is set;
    // before mount so it runs ahead of any login submit.
    syncDeviceId()

    // Configure Arvo if available
    if (window?.ww?.vueStartingState?.arvoConfig != null) {
      void configureArvo(window.ww.vueStartingState.arvoConfig)
    }

    // Mobile browsers handle 100% height in ways we don't like. Fix that.
    fixMobileViewport()

    // Evaluate props only after DOMContentLoaded to ensure scripts that set globals on `window` have run.
    const props = typeof options.rootComponentProps === 'function' ? options.rootComponentProps() : {}

    // Create Vue 3 app
    vueApp = createApp(options.rootComponent, props) as VueApp
    // We re-assign window.app since in vue3, window.app is initialized to be the dom node instead of the app instance
    window.app = vueApp

    // Add design classes to component root elements if ?injectDesignClasses query param is present
    injectDesignClassesIfEnabled(vueApp)

    // Setup stores
    const { vuexStore, piniaStore } = setupStores(vueApp, options)

    // Register Vuex store if available
    if (vuexStore !== undefined && vuexStore !== null) {
      // Add store to global properties for backward compatibility
      vueApp.config.globalProperties.$store = vuexStore
      window.app.$store = vuexStore
    }

    // Register Pinia store if available
    if (piniaStore !== undefined && piniaStore !== null) {
      vueApp.use(piniaStore)
    }

    // Set up lifecycle hooks
    if (options.created !== undefined && options.created !== null) {
      vueApp.config.globalProperties.$created = options.created
    }

    if (options.mounted !== undefined && options.mounted !== null) {
      vueApp.config.globalProperties.$mounted = options.mounted
    }

    // Apply other Vue options
    if (
      options.otherVueOptions !== undefined &&
      options.otherVueOptions !== null &&
      Object.keys(options.otherVueOptions).length > 0
    ) {
      Object.entries(options.otherVueOptions).forEach(([key, value]) => {
        if (vueApp != null) {
          vueApp.config.globalProperties[`$${key}`] = value
        }
      })
    }

    // Setup error tracking
    setupErrorTracking(vueApp, options)

    // Setup internationalization
    setupIntl(vueApp)

    // Setup plugins
    await setupPlugins(vueApp, options)

    // Setup Vue Query if needed
    if (options.useVueQuery === true && vueApp) {
      const queryModules = await Promise.all([import('@tanstack/vue-query'), import('@@/bits/query_client')])
      const vueQueryModule = queryModules[0]
      const queryClientModule = queryModules[1]
      const queryClient = await queryClientModule.createQueryClient()

      vueApp.use(vueQueryModule.VueQueryPlugin, { queryClient })
      window.queryClient = queryClient
    }

    // Configure Vue app
    if (vueApp && process.env.NODE_ENV === 'production' && isDebugMode) {
      vueApp.config.performance = true
    } else if (vueApp) {
      vueApp.config.performance = hasPerfUrlParam()
    }

    // Set ignored elements
    // if (vueApp) {
    //   vueApp.config.compilerOptions = {
    //     isCustomElement: (tag) =>
    //       ['trix-editor', 'lottie-player', 'dotlottie-wc', MENTION_ELEMENT_TAG_NAME].includes(tag),
    //   }
    // }

    // Initialize Pinia store.
    // Assign window.app.$pinia before initializePiniaStore; setups from that callback read it
    // (surface.ts:62 addressListener) and see undefined until here though window.app already exists.
    if (vueApp && piniaStore != null && options.usePinia === true) {
      window.app.$pinia = piniaStore
    }
    if (
      vueApp &&
      options.usePinia === true &&
      typeof options.initializePiniaStore === 'function' &&
      piniaStore != null
    ) {
      options.initializePiniaStore(piniaStore)
    }

    if (options.realtime === true) {
      vuexStore?.dispatch('realtime/connect')
    }

    // Initialize authentication channel
    initializeAuthenticationChannel({
      onLogin: options.onGlobalLogin,
      onLogout: options.onGlobalLogout,
    })

    // Fetch the adapter only inside the desktop app. A static import ships zod to every web page.
    // Do not await the chunk. A failed adapter fetch must not block mount.
    if (device.desktopApp) {
      void import('@@/desktop_shell/install')
        .then(({ installDesktopShellForPage }) => {
          installDesktopShellForPage(options.buildApplicationMenu, { switchToMakePage: options.switchToMakePage })
        })
        .catch((error: unknown) => {
          errorTracker.captureException(error)
        })
    }

    // window.app ??= vueApp
    // Mount the app if an element is specified
    if (typeof options.el === 'string' && options.el.length > 0) {
      // Avoid extra `div` wrapper after mounting the app.
      // @see https://v3-migration.vuejs.org/breaking-changes/mount-changes.html
      const fragment = document.createDocumentFragment()
      window.PadletBootStage = 'mount-called'
      vueApp.mount(fragment)

      // Initialize PostHog
      void setupPostHog(vueApp)

      const target = document.querySelector(options.el)
      if (target == null || target.parentNode == null) {
        throw new Error('Unable to find element to mount Vue app.')
      }
      target?.parentNode?.replaceChild(fragment, target)
      window.PadletAssetLoadRecovery?.markAppMounted?.()
      void import('@@/bits/translation_feedback/mount')
        .then(({ mountTranslationFeedbackIfEnabled }) => mountTranslationFeedbackIfEnabled())
        .catch(() => {})
      // Exposes our MCP tools to a browser-resident agent via WebMCP. Gated on the
      // accessor existing so browsers without the WebMCP flag — i.e. nearly all of
      // them — do not even fetch the chunk; a bare `in` rather than a property read,
      // so it costs no deprecation warning either.
      //
      // The fetch path, for catalogues that are not inlined: signed-in visitors and any
      // page without the tag. A logged-out visitor is already registered above.
      //
      // Deferred to idle, chunk fetch included: nothing on the page waits for this,
      // and no agent is reading during first paint. It also lands after the full
      // client surface has injected its csrf-token meta tag, which it fetches at
      // runtime rather than rendering server-side.
      if ('modelContext' in document || 'modelContext' in navigator) {
        requestIdleCallback(
          () => {
            void import('@@/bits/model_context')
              .then(({ registerModelContextTools }) => registerModelContextTools())
              .catch(() => {})
          },
          { timeout: 3000 },
        )
      }
    }
  } catch (e) {
    if (e instanceof PageReloadableError) {
      reload()
    } else {
      errorTracker.captureException(e)
    }
    return undefined
  }

  return vueApp
}

function setupErrorTracking(app: App, options: VueAppOptions): void {
  errorTracker.init({
    vue: app,
    environment: options.environment ?? environment,
    locale: options.locale ?? getHyphenatedCurrentLocale(),
    release: options.release ?? document?.documentElement?.dataset?.version,
    source: options.errorTrackingSource,
  })
}

function setupIntl(app: App): void {
  app.config.globalProperties.__ = __
  app.config.globalProperties.N__ = N__
  app.config.globalProperties.p__ = p__
  app.config.globalProperties.n__ = n__
  app.config.globalProperties.np__ = np__
}

async function setupPlugins(app: App, options: VueAppOptions): Promise<void> {
  // Accessibility tools (in development) are opt-in via the axe=true url param;
  // disableAccessibilityChecks remains an explicit per-app opt-out
  const disableAccessibilityChecks = options.disableAccessibilityChecks ?? false
  if (!disableAccessibilityChecks) void useVueAxePlugin(app)

  // Use other page-specific plugins
  if (options.loadPlugins != null && typeof options.loadPlugins === 'function') {
    await options.loadPlugins(app)
  }
}

/**
 * vue-axe is opt-in via the axe=true url param because its full-document scans
 * noticeably slow down local development.
 * perf=true is a url param that disables logging and axe in development
 * this is useful for debugging performance issues; it wins over axe=true
 * @see @@/bits/dev_url_params
 */
async function useVueAxePlugin(app: App): Promise<void> {
  if (environment === 'production') return
  if (!hasAxeUrlParam()) return
  if (hasPerfUrlParam()) return
  const VueAxePlugin = (await import('vue-axe')).default
  app.use(VueAxePlugin, {
    allowConsoleClears: false,
    clearConsoleOnUpdate: false,
  })
}

function setupVuexStore(app: App, options: VueAppOptions): VuexStore<any> | undefined {
  // Allow already-initialized store for now, to make porting Surface easier.
  let store = options.store

  if (isObject(options.storeOptions)) {
    store = createStore(options.storeOptions)
  }

  if (store !== undefined && typeof options.initializeStore === 'function') {
    options.initializeStore(store)
    app.use(store)
  }

  return store
}

function setupPiniaStore(): PiniaStore | undefined {
  const store = createPinia()

  return store
}

function setupStores(
  app: App,
  options: VueAppOptions,
): {
  vuexStore: VuexStore<any> | undefined
  piniaStore: PiniaStore | undefined
} {
  const usePinia = options.usePinia ?? false
  const vuexStore = setupVuexStore(app, options)
  const piniaStore = usePinia ? setupPiniaStore() : undefined

  return { vuexStore, piniaStore }
}

async function setupPostHog(app: VueApp): Promise<void> {
  const posthogEnabled = isAppUsing('posthogNpsSurvey') || isAppUsing('posthogSessionRecording')
  if (posthogEnabled && app.$posthog == null) {
    const { usePostHog } = await import('@@/vuecomposables/posthog')
    const { posthog } = await usePostHog()
    app.$posthog = posthog
  }
}

type IdArrayMap = Record<Id, Id[]>
function stableSync(target: IdArrayMap, source: IdArrayMap): IdArrayMap {
  new Set([...Object.keys(source), ...Object.keys(target)]).forEach(function (key) {
    const sourceIds = source[key]
    const targetIds = target[key]

    if (sourceIds == null && targetIds == null) {
      return
    }

    if (sourceIds != null && targetIds != null && isEqual(sourceIds, targetIds)) {
      return
    }

    if (sourceIds != null) {
      target[key] = [...sourceIds]
    } else {
      // Use Reflect.deleteProperty instead of delete operator
      Reflect.deleteProperty(target, key)
    }
  })
  return target
}

/**********************
 * Unbounded watchers *
 **********************

 These watcher functions register the watcher asynchronously so that
 they are not bound to any particular component instance. This is
 useful in context like a Pinia store where the store may be used
 in multiple components but you don't want the watchers in the store
 to be stopped automatically when a component is unmounted.

 Read more about it here: https://vuejs.org/guide/essentials/watchers.html#stopping-a-watcher

 To preserve the type safety of these custom watchers, I copied the
 function signatures from node_modules/vue/types/v3-generated.d.ts
 and only changed the return type to be Promise<WatchStopHandle>.
 **********************/

type MultiWatchSources = Array<WatchSource<unknown> | object>

type MapSources<T, Immediate> = {
  [K in keyof T]: T[K] extends WatchSource<infer V>
    ? Immediate extends true
      ? V | undefined
      : V
    : T[K] extends object
      ? Immediate extends true
        ? T[K] | undefined
        : T[K]
      : never
}

async function unboundedWatch<T extends MultiWatchSources, Immediate extends Readonly<boolean> = false>(
  sources: [...T],
  cb: WatchCallback<MapSources<T, false>, MapSources<T, Immediate>>,
  options?: WatchOptions<Immediate>,
): Promise<WatchStopHandle>

async function unboundedWatch<T extends Readonly<MultiWatchSources>, Immediate extends Readonly<boolean> = false>(
  source: T,
  cb: WatchCallback<MapSources<T, false>, MapSources<T, Immediate>>,
  options?: WatchOptions<Immediate>,
): Promise<WatchStopHandle>

async function unboundedWatch<T, Immediate extends Readonly<boolean> = false>(
  source: WatchSource<T>,
  cb: WatchCallback<T, Immediate extends true ? T | undefined : T>,
  options?: WatchOptions<Immediate>,
): Promise<WatchStopHandle>

async function unboundedWatch<T extends object, Immediate extends Readonly<boolean> = false>(
  source: T,
  cb: WatchCallback<T, Immediate extends true ? T | undefined : T>,
  options?: WatchOptions<Immediate>,
): Promise<WatchStopHandle>

async function unboundedWatch(
  sourceOrSources: any,
  cb: WatchCallback,
  options?: WatchOptions,
): Promise<WatchStopHandle> {
  return await new Promise((resolve) => {
    setTimeout(() => {
      const stopHandle = watch(sourceOrSources, cb, options)
      resolve(stopHandle)
    }, 0)
  })
}

async function unboundedWatchEffect(effect: WatchEffect, options?: WatchOptionsBase): Promise<WatchStopHandle> {
  return await new Promise((resolve) => {
    setTimeout(() => {
      const stopHandle = watchEffect(effect, options)
      resolve(stopHandle)
    }, 0)
  })
}

/**
 * Direct property setter for Vue 3 reactivity
 *
 * In Vue 3, we can directly set properties on reactive objects due to the Proxy-based reactivity system.
 * This function maintains the same signature as Vue 2's Vue.set for backward compatibility.
 *
 * @param target The object or array to set the property on
 * @param key The property name or array index
 * @param value The value to set
 * @returns The set value
 */
function vSet<T, K extends keyof T>(target: T, key: K, value: T[K]): T[K]
function vSet<T>(target: T[], key: number, value: T): T
function vSet(target: object, key: string | number, value: any): any {
  // Simply set the property directly - Vue 3's reactivity system will handle the rest
  target[key] = value
  return value
}

/**
 * Direct property deletion for Vue 3 reactivity
 *
 * In Vue 3, we can directly delete properties on reactive objects due to the Proxy-based reactivity system.
 * This function maintains the same signature as Vue 2's Vue.delete for backward compatibility.
 *
 * @param target The object or array to delete the property from
 * @param key The property name or array index
 */
function vDel<T, K extends keyof T>(target: T, key: K): void
function vDel<T>(target: T[], key: number): void
function vDel(target: object, key: string | number): void {
  if (Array.isArray(target) && typeof key === 'number') {
    // For arrays, use splice to properly remove the element
    target.splice(key, 1)
  } else {
    // For objects, use Reflect.deleteProperty
    Reflect.deleteProperty(target, key)
  }
}

export {
  defineAsyncComponent,
  PageReloadableError,
  registerInlinedModelContextTools,
  setupIntl,
  setupPlugins,
  setupVueApp,
  stableSync,
  unboundedWatch,
  unboundedWatchEffect,
  vDel,
  vSet,
}
export type { VueApp }
